/**
 * 파일명 : hom0101m.tsx
 * 설명 : 사업장관리 탭 사업장 목록 조회 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-04-07   이경원 외 2명     최초작성
 * 1.1       2023-05-12   장용, 황은택
 */

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Hom0101mAction } from '../../../redux/slice/hom/hom0101m_slice';
import { IBusinessState, IInquiryCndsState } from '../../../redux/models/hom/hom0101m_model';

/* AntD */
import { Button, Space, Input, Row, Col, Layout, Typography, Collapse, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

const Hom0101m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
     const dispatch = useAppDispatch();
     const inquiryCnds  = useAppSelector((state) => state.Hom0101m.inquiryCnds);
     const businessList = useAppSelector((state) => state.Hom0101m.businessList);
     const current      = useAppSelector((state) => state.Hom0101m.current);
     const pageSize     = useAppSelector((state) => state.Hom0101m.pageSize);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        onClickSearch();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;

   
    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>기관코드</div>,
                dataIndex: 'companyInstCd',
                key: 'companyInstCd',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사업장명</div>,
                dataIndex: 'instNm',
                key: 'instNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>관리자명</div>,
                dataIndex: 'managerNm',
                key: 'managerNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>주소</div>,
                dataIndex: 'address',
                key: 'address',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>연락처</div>,
                dataIndex: 'poneNumber',
                key: 'poneNumber',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>팩스번호</div>,
                dataIndex: 'faxNumber',
                key: 'faxNumber',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>이메일</div>,
                dataIndex: 'email',
                key: 'email',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>홈페이지 노출여부</div>,
                dataIndex: 'useYn',
                key: 'useYn',
                align: 'center',
                width: '10%',
                render: (text:string) => text === 'Y' ? '노출' : '미노출'
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (record:IBusinessState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '9%',
            }];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Hom0101mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Hom0101mAction.setBusinessList([]));
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IBusinessState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                onClickSearch();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_addBiz': {
                const params:any = {};
                params.title = '사업장등록';
                params.popupId = 'hom0101p';
                params.accept = fn_Search;
                params.width = 1100;
                common.openPopup(params);
                break;

            }
            case 'btn_showDetail': {
                const params:any = {};
                params.title = '사업장관리';
                params.popupId = 'hom0101p';
                params.accept = fn_Search;
                params.width = 1100;
                params.props = { companyInstCd : record.companyInstCd };
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회-검색어를 입력 후 조회를 눌렀을 때 실행
     * param : 
     */
    const onClickSearch = () => {
        dispatch(Hom0101mAction.setCurrent(1));
        fn_Search();
    };

    /**
     * 기능 : 사업장 등록 및 상세조회 후 닫았을 때 실행, 위의 search와 페이징 차이
     * param : 
     */
    const fn_Search = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRHOMR0101',
            success: (responseData:any) => {
                dispatch(Hom0101mAction.setBusinessList(responseData.businessItems));
            },
        });
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
     const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space>
                                        <label>사업장명</label>
                                        <Input
                                            placeholder='입력하세요'
                                            style={{ width: 180 }}
                                            value={inquiryCnds.instNm}
                                            onKeyUp ={handleOnKeyUp}
                                            onChange={(e:any) => {
                                                dispatch(Hom0101mAction.setInquiryCnds({ ...inquiryCnds, instNm : e.currentTarget.value }))
                                            }}
                                        />
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            초기화
                                    </Button>
                                        {common.isAdmin() ?
                                        <Button
                                        className='background-color-default-button'
                                        onClick={(e:any) => onClick(e, null)}
                                        name='btn_addBiz'
                                        >
                                        사업장등록
                                    </Button>  : null}
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            조회
                                    </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>사업장 목록</Text>
                            </Space>
                        </Col>
                        <Col flex='auto' style={{ marginRight: 15 }}>
                        </Col>
                    </Row>
                    <Table
                        dataSource={businessList}
                        key='no'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + businessList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Hom0101mAction.setCurrent(page));
                                dispatch(Hom0101mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Hom0101m;